import * as React from 'react';
import moment from 'moment-timezone';
import classnames from 'classnames';
import { Field, Form, FormRenderProps } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import FontAwesomeIcon from 'react-fontawesome';
import { ValidationErrors } from 'final-form';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import { Store, StoreConfiguration } from './types';
import { enqueueNotification, updateStoreConfiguration } from './actions/Actions';
import { CLOUDINARY_BASE_URL, STORE_CONFIG_NS } from './Constants';
import ConditionalField from './ConditionalField';

type FormValues = Partial<StoreConfiguration>;

const Configuration: React.FC<RouteComponentProps> = () => {
    const dispatch: ThunkDispatch<Store, unknown, AnyAction> = useDispatch();
    const storeConfiguration = useSelector(({ app }: Store) => app.storeConfiguration);
    const availableLanguages = useSelector(({ app }: Store) => app.availableLanguages);
    const { t } = useTranslation();

    const getInitialFormValues = React.useCallback(
        (configuration = storeConfiguration): FormValues => {
            const {
                canUserPayWithCard,
                hasConfiguredLogo,
                hasOrder2FaAuthenticationEnabled,
                hasOrderGeolocationActivated,
                isCardReaderMobile,
                providesFreeWifi,
                hasSmartFeaturesEnabled
            } = configuration;

            return {
                ...configuration,
                canUserPayWithCard: canUserPayWithCard || false,
                hasConfiguredLogo: hasConfiguredLogo || false,
                hasOrder2FaAuthenticationEnabled:
                    hasOrder2FaAuthenticationEnabled || false,
                hasOrderGeolocationActivated: hasOrderGeolocationActivated || false,
                isCardReaderMobile: isCardReaderMobile || false,
                providesFreeWifi: providesFreeWifi || false,
                hasSmartFeaturesEnabled: hasSmartFeaturesEnabled || false
            };
        },
        [storeConfiguration]
    );

    const [loading, setLoading] = React.useState(false);
    const [formValues, setFormValues] = React.useState<FormValues>(
        getInitialFormValues()
    );
    const [selectedLanguages, setSelectedLanguages] = React.useState(
        storeConfiguration.selectedLanguages
    );
    const [defaultUILanguageID, setDefaultUILanguageID] = React.useState(
        storeConfiguration.defaultUILanguageID
    );
    const [defaultMenuLanguageID, setDefaultMenuLanguageID] = React.useState(
        storeConfiguration.defaultMenuLanguageID
    );
    const [selectedLogo, setSelectedLogo] = React.useState<File>(null);

    const validateFormValues = React.useCallback(
        (values: FormValues) => {
            const errors: ValidationErrors = {};
            const languages = values.selectedLanguages.filter(
                ({ isSelectedUI }) => isSelectedUI
            );

            if (!languages.length) {
                errors.selectedLanguages = t(`${STORE_CONFIG_NS}.languages_validation`);
            }

            if (
                !languages.find(
                    ({ languageID }) => languageID === values.defaultUILanguageID
                )
            ) {
                errors.defaultUILanguageID = t(
                    `${STORE_CONFIG_NS}.default_lang_ui_validation`
                );
            }
            if (
                !languages.find(
                    ({ languageID }) => languageID === values.defaultMenuLanguageID
                )
            ) {
                errors.defaultMenuLanguageID = t(
                    `${STORE_CONFIG_NS}.default_lang_ui_validation`
                );
            }

            return errors;
        },
        [t]
    );

    const setFile = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedLogo(e.target.files[0]);
    }, []);

    const handleDefaultLanguageChange = React.useCallback(
        (id: number, values: FormValues) => () => {
            setDefaultUILanguageID(id);
            setFormValues(values);
        },
        []
    );

    const handleDefaultMenuLanguageChange = React.useCallback(
        (id: number, values: FormValues) => () => {
            setDefaultMenuLanguageID(id);
            setFormValues(values);
        },
        []
    );

    const handleSelectedLanguagesChange = React.useCallback(
        (id: number, values: FormValues) => () => {
            setFormValues(values);
            setSelectedLanguages(
                selectedLanguages.map(s =>
                    s.languageID === id ? { ...s, isSelectedUI: !s.isSelectedUI } : s
                )
            );
        },
        [selectedLanguages]
    );

    const renderForm = React.useCallback(
        ({ handleSubmit, submitting, validating, values }: FormRenderProps) => {
            const disabled = submitting || validating;

            return (
                <form onSubmit={handleSubmit} className='form'>
                    <div className='flex-container'>
                        <label className='form-field'>
                            <label className='wide'>
                                {t(`${STORE_CONFIG_NS}.card_payment`)}:
                            </label>
                            <Field name='canUserPayWithCard' type='checkbox'>
                                {({ input }) => (
                                    <div className='field-input'>
                                        <input {...input} />
                                    </div>
                                )}
                            </Field>
                        </label>
                        <ConditionalField when='canUserPayWithCard' is>
                            <label className='form-field'>
                                <label className='wide'>
                                    {t(`${STORE_CONFIG_NS}.pos_devices`)}:
                                </label>
                                <Field name='cardReaderAmount' defaultValue={0}>
                                    {({ input, meta }) => (
                                        <div className='field-input'>
                                            <input {...input} type='number' min={0} />
                                            {meta.error && meta.touched && (
                                                <FontAwesomeIcon
                                                    className='error-icon'
                                                    name='exclamation-circle'
                                                    title={meta.error}
                                                />
                                            )}
                                        </div>
                                    )}
                                </Field>
                            </label>
                        </ConditionalField>
                    </div>
                    <label className='form-field'>
                        <label className='wide'>
                            {t(`${STORE_CONFIG_NS}.pos_movable`)}:
                        </label>
                        <Field name='isCardReaderMobile' type='checkbox'>
                            {({ input }) => (
                                <div className='field-input'>
                                    <input {...input} />
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='wide'>
                            {t(`${STORE_CONFIG_NS}.free_wifi`)}:
                        </label>
                        <Field name='providesFreeWifi' type='checkbox'>
                            {({ input }) => (
                                <div className='field-input'>
                                    <input {...input} />
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='wide'>
                            {t(`${STORE_CONFIG_NS}.customer_ordering`)}:
                        </label>
                        <Field name='hasSmartFeaturesEnabled' type='checkbox'>
                            {({ input }) => (
                                <div className='field-input'>
                                    <input {...input} />
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='wide'>
                            {t(`${STORE_CONFIG_NS}.geolocation`)}:
                        </label>
                        <Field name='hasOrderGeolocationActivated' type='checkbox'>
                            {({ input }) => (
                                <div className='field-input'>
                                    <input {...input} />
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='wide'>
                            {t(`${STORE_CONFIG_NS}.authentication`)}:
                        </label>
                        <Field name='hasOrder2FaAuthenticationEnabled' type='checkbox'>
                            {({ input }) => (
                                <div className='field-input'>
                                    <input {...input} />
                                </div>
                            )}
                        </Field>
                    </label>
                    <div className='flex-container'>
                        <label className='form-field'>
                            <label className='wide'>
                                {t(`${STORE_CONFIG_NS}.logo`)}:
                            </label>
                            <Field name='hasConfiguredLogo' type='checkbox'>
                                {({ input }) => (
                                    <div className='field-input'>
                                        <input {...input} />
                                    </div>
                                )}
                            </Field>
                        </label>
                        <ConditionalField when='hasConfiguredLogo' is>
                            <label className='form-field'>
                                <div className='field-input'>
                                    <input
                                        type='file'
                                        onChange={setFile}
                                        accept='.jpg, .png, .jpeg'
                                    />
                                </div>
                            </label>
                        </ConditionalField>
                    </div>
                    <div className='languages'>
                        <label className='form-field'>
                            <label className='wide'>
                                {t(`${STORE_CONFIG_NS}.languages`)}:
                            </label>
                            <Field
                                name='selectedLanguages'
                                // eslint-disable-next-line react/jsx-no-bind
                                render={({ input, meta }) => (
                                    <>
                                        <div className='language-container'>
                                            {availableLanguages.map(
                                                ({ name, code, id }) => {
                                                    const { isSelectedUI } =
                                                        input.value.find(
                                                            ({ languageID }) =>
                                                                languageID === id
                                                        );

                                                    return (
                                                        <div
                                                            className={classnames(
                                                                'language',
                                                                {
                                                                    selected:
                                                                        isSelectedUI
                                                                }
                                                            )}
                                                            key={id}
                                                            onClick={handleSelectedLanguagesChange(
                                                                id,
                                                                values
                                                            )}>
                                                            <img
                                                                className='flag'
                                                                src={`${CLOUDINARY_BASE_URL}/generic/languages/${code}.png`}
                                                            />
                                                            {name}
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </div>
                                        {meta.error && (
                                            <FontAwesomeIcon
                                                className='error-icon'
                                                name='exclamation-circle'
                                                title={meta.error}
                                            />
                                        )}
                                    </>
                                )}
                            />
                        </label>
                    </div>
                    <div className='flex-container languages'>
                        <label className='form-field'>
                            <label className='wide'>
                                {t(`${STORE_CONFIG_NS}.ui_lang`)}:
                            </label>
                            <Field
                                name='defaultUILanguageID'
                                // eslint-disable-next-line react/jsx-no-bind
                                render={({ input, meta }) => (
                                    <>
                                        <div className='language-container'>
                                            {availableLanguages.map(
                                                ({ name, code, id }) => (
                                                    <div
                                                        className={classnames(
                                                            'language',
                                                            {
                                                                selected:
                                                                    input.value === id
                                                            }
                                                        )}
                                                        key={id}
                                                        onClick={handleDefaultLanguageChange(
                                                            id,
                                                            values
                                                        )}>
                                                        <img
                                                            className='flag'
                                                            src={`${CLOUDINARY_BASE_URL}/generic/languages/${code}.png`}
                                                        />
                                                        {name}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                        {meta.error && (
                                            <FontAwesomeIcon
                                                className='error-icon'
                                                name='exclamation-circle'
                                                title={meta.error}
                                            />
                                        )}
                                    </>
                                )}
                            />
                        </label>
                        <label className='form-field'>
                            <label className='wide'>
                                {t(`${STORE_CONFIG_NS}.menu_lang`)}:
                            </label>
                            <Field
                                name='defaultMenuLanguageID'
                                // eslint-disable-next-line react/jsx-no-bind
                                render={({ input, meta }) => (
                                    <>
                                        <div className='language-container'>
                                            {availableLanguages.map(
                                                ({ name, code, id }) => (
                                                    <div
                                                        className={classnames(
                                                            'language',
                                                            {
                                                                selected:
                                                                    input.value === id
                                                            }
                                                        )}
                                                        key={id}
                                                        onClick={handleDefaultMenuLanguageChange(
                                                            id,
                                                            values
                                                        )}>
                                                        <img
                                                            className='flag'
                                                            src={`${CLOUDINARY_BASE_URL}/generic/languages/${code}.png`}
                                                        />
                                                        {name}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                        {meta.error && (
                                            <FontAwesomeIcon
                                                className='error-icon'
                                                name='exclamation-circle'
                                                title={meta.error}
                                            />
                                        )}
                                    </>
                                )}
                            />
                        </label>
                    </div>
                    <label className='form-field'>
                        <label className='wide'>
                            {t(`${STORE_CONFIG_NS}.people_table`)}:
                        </label>
                        <Field name='defaultAllowedPeoplePerTable'>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <input {...input} type='number' min={1} />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <div className='action-buttons'>
                        <div>
                            <button className='save' type='submit' disabled={disabled}>
                                {t('save')}
                            </button>
                            {loading && (
                                <FontAwesomeIcon name='spinner' className='fa-spin' />
                            )}
                        </div>
                    </div>
                </form>
            );
        },
        [
            availableLanguages,
            handleDefaultLanguageChange,
            handleDefaultMenuLanguageChange,
            handleSelectedLanguagesChange,
            loading,
            setFile,
            t
        ]
    );

    const uploadLogo = React.useCallback(
        (shortUniqueID: string, filename: string) => {
            if (!selectedLogo) {
                return Promise.resolve({});
            }

            const data = new FormData();
            data.append('file', selectedLogo);
            data.append('upload_preset', 'storeLogos');
            data.append('cloud_name', 'scanandorder');
            data.append('folder', `stores/store___${shortUniqueID}`);
            data.append('public_id', filename);

            return fetch('https://api.cloudinary.com/v1_1/scanandorder/image/upload', {
                method: 'POST',
                body: data
            }).then(res => res.json());
        },
        [selectedLogo]
    );

    const handleFormSubmit = React.useCallback(
        (values: FormValues) => {
            if (values.hasConfiguredLogo && !selectedLogo) {
                dispatch(
                    enqueueNotification({
                        key: new Date().getTime(),
                        message: t(`${STORE_CONFIG_NS}.missing_logo`),
                        options: { variant: 'error' }
                    })
                );
                return;
            }

            setLoading(true);

            const filename = `logo_upload_${moment().format(
                'YYYY_MM_DD_HH_mm_ss_SSSS'
            )}`;

            uploadLogo(values.shortUniqueID, filename).then(res => {
                if (res.error) {
                    console.error(res.error.message);
                    dispatch(
                        enqueueNotification({
                            key: new Date().getTime(),
                            message: t(`${STORE_CONFIG_NS}.upload_logo_error`),
                            options: { variant: 'error' }
                        })
                    );
                    return;
                }

                const configuration = {
                    ...values,
                    logoUploadName: selectedLogo
                        ? `${filename}.${selectedLogo.name.split('.').pop()}`
                        : null
                };

                if (!values.canUserPayWithCard) {
                    configuration.cardReaderAmount = 0;
                }

                dispatch(updateStoreConfiguration(configuration))
                    .then(config => {
                        setFormValues(getInitialFormValues(config));
                        setSelectedLanguages(config.selectedLanguages);
                        setDefaultUILanguageID(config.defaultUILanguageID);
                        setDefaultMenuLanguageID(config.defaultMenuLanguageID);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            });
        },
        [dispatch, getInitialFormValues, selectedLogo, t, uploadLogo]
    );

    return (
        <div className='admin-form main-content'>
            <Sidebar />
            <div className='store-configuration-form'>
                <Form
                    render={renderForm}
                    onSubmit={handleFormSubmit}
                    validate={validateFormValues}
                    initialValues={{
                        ...formValues,
                        selectedLanguages,
                        defaultUILanguageID,
                        defaultMenuLanguageID
                    }}
                />
            </div>
        </div>
    );
};

export default Configuration;
